import React, { useMemo } from 'react';
import { fetchGeoJsonData, fetchBlockOutlineData } from "../../utils/firebase";
import './tileStyleSheets/mapTile.css';
import CheckIcon from "@mui/icons-material/Check";

export const MAP_DEFAULT_PROPS = {
    mode: 'overview',
    showOutline: true,
    showBlocks: true,
    showBlockLabels: true,
    showFaults: true,
    legend: true,

    //fault information/details
    faultColorBy: 'level',
    levels: { 'Level 1': true, 'Level 2': true, 'Level 3': true },
    categories: null,
    faultSize: 2,
    maxFaults: null,

    //map layout settings
    size: 'medium',
    padding: 16,
    margin: 12,

    //for the mode that shows each individual block
    blocksPerRow: 2,
    blocksLimit: null,
    siteTitle: null,
    subtitle: null,
    panelFill: "#808080",
    panelStroke: "#333",

    // subset of blocks in individual block map view mode
    selectedBlocks: [],
    showPanelsInBlocks: true,
};

const SIZE_TO_HEIGHT = { small: 320, medium: 550, large: 680 };
const BLOCK_TILE_H = { small: 180, medium: 270, large: 360 };

const LEVEL_COLORS = {
    'Level 1': '#E0B000',
    'Level 2': '#E05600',
    'Level 3': '#E00000',
};
const CATEGORY_PALETTE = ['#2563eb', '#db2777', '#059669', '#9333ea', '#f43f5e', '#0ea5e9', '#eab308', '#22c55e', '#7c3aed'];

// geometry functions & helper functions
function pathFromPolygon(coords, project) {
    const rings = coords.map(ring => ring.map(([lon, lat], i) => {
        const { x, y } = project(lon, lat);
        return `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ') + ' Z');
    return rings.join(' ');
}
function featurePath(feature, project) {
    const g = feature?.geometry;
    if (!g) return '';
    if (g.type === 'Polygon') return pathFromPolygon(g.coordinates, project);
    if (g.type === 'MultiPolygon') {
        return g.coordinates.map(poly => pathFromPolygon(poly, project)).join(' ');
    }
    return '';
}
function centroidOf(feature) {
    const g = feature?.geometry;
    let sumX = 0, sumY = 0, n = 0;
    const tap = (lon, lat) => { sumX += lon; sumY += lat; n++; };
    if (g?.type === 'Polygon') {
        g.coordinates[0]?.forEach(([lon, lat]) => tap(lon, lat));
    } else if (g?.type === 'MultiPolygon') {
        g.coordinates.forEach(poly => poly[0]?.forEach(([lon, lat]) => tap(lon, lat)));
    }
    if (!n) return { lon: 0, lat: 0 };
    return { lon: sumX / n, lat: sumY / n };
}
function bboxOf(feature) {
    const g = feature?.geometry;
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
    const tap = (lon, lat) => {
        if (lon < minLon) minLon = lon;
        if (lat < minLat) minLat = lat;
        if (lon > maxLon) maxLon = lon;
        if (lat > maxLat) maxLat = lat;
    };
    if (g?.type === 'Polygon') {
        g.coordinates.forEach(r => r.forEach(([lon, lat]) => tap(lon, lat)));
    } else if (g?.type === 'MultiPolygon') {
        g.coordinates.forEach(poly => poly.forEach(r => r.forEach(([lon, lat]) => tap(lon, lat))));
    }
    if (minLon === Infinity) return null;
    return [minLon, minLat, maxLon, maxLat];
}
function unionBbox(bboxes) {
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
    bboxes.forEach(b => {
        if (!b) return;
        if (b[0] < minLon) minLon = b[0];
        if (b[1] < minLat) minLat = b[1];
        if (b[2] > maxLon) maxLon = b[2];
        if (b[3] > maxLat) maxLat = b[3];
    });
    if (minLon === Infinity) return null;
    return [minLon, minLat, maxLon, maxLat];
}
function fitProjector(bbox, width, height, pad = 16) {
    if (!bbox) return () => ({ x: 0, y: 0 });
    const [minLon, minLat, maxLon, maxLat] = bbox;
    const w = Math.max(1e-9, maxLon - minLon);
    const h = Math.max(1e-9, maxLat - minLat);
    const innerW = Math.max(1, width - pad * 2);
    const innerH = Math.max(1, height - pad * 2);
    const sx = innerW / w;
    const sy = innerH / h;
    const s = Math.min(sx, sy);
    const ox = (width - (w * s)) / 2 - (minLon * s);
    const oy = (height - (h * s)) / 2 + (maxLat * s); // y flipped
    return (lon, lat) => ({ x: lon * s + ox, y: -lat * s + oy });
}
function hashColor(str) {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
    const idx = Math.abs(h) % CATEGORY_PALETTE.length;
    return CATEGORY_PALETTE[idx];
}
function guessBlockId(props) {
    const p = props || {};
    return p.IB ?? p.ib ?? p.block ?? p.Block ?? p.name ?? p.id ?? null;
}
function normalizeKey(v) {
    return v ? String(v).trim().toUpperCase() : null;
}
function faultIBfromLocation(loc) {
    if (!loc || typeof loc !== 'string') return null;
    const dash = loc.indexOf('-');
    return (dash > 0) ? loc.slice(0, dash) : null;
}

//data helper functions 
export function useBlockFeatures(blockOutline) {
    return useMemo(() => {
        const feats = Array.isArray(blockOutline?.features) ? blockOutline.features : [];
        const blocks = feats.map((f) => {
            const id = guessBlockId(f.properties || {});
            return { id, feature: f, bbox: bboxOf(f), centroid: centroidOf(f) };
        }).filter(b => !!b.id);
        const siteBbox = unionBbox(blocks.map(b => b.bbox));
        return { blocks, siteBbox };
    }, [blockOutline]);
}
function useFilteredFaults(faults, props) {
    return useMemo(() => {
        if (!Array.isArray(faults)) return [];
        const levelOn = props.levels || {};
        const catFilter = props.categories && props.categories.length ? new Set(props.categories) : null;

        const rows = faults.filter(f => {
            const lvl = String(f.level || f.Level || '').trim() || 'Unknown';
            if (lvl !== 'Unknown' && levelOn && levelOn[lvl] === false) return false;
            if (catFilter) {
                const cat = decodeURIComponent(f.category || f.Category || '').trim();
                if (!catFilter.has(cat)) return false;
            }
            return true;
        });

        return (typeof props.maxFaults === 'number' && props.maxFaults > 0)
            ? rows.slice(0, props.maxFaults)
            : rows;
    }, [faults, props.levels, props.categories, props.maxFaults]);
}

// Map Legend
function Legend({ mode = 'level', levels, categoriesShown }) {
    const items = mode === 'level'
        ? Object.keys(levels || {}).filter(k => levels[k] !== false)
            .map(k => ({ key: k, color: LEVEL_COLORS[k] || '#374151', label: k }))
        : (categoriesShown || []).map(c => ({ key: c, color: hashColor(c), label: c }));

    if (!items.length) return null;
    return (
        <div style={{
            position: 'absolute', right: 8, top: 8, background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: 6, padding: '6px 8px', fontSize: 12, lineHeight: 1.2
        }}>
            {items.map(it => (
                <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '3px 0' }}>
                    <span style={{ width: 10, height: 10, borderRadius: 9999, background: it.color }} />
                    <span>{it.label}</span>
                </div>
            ))}
        </div>
    );
}

//GPS dots for overview
function OverviewSVG({ blockOutline, blocks, faults, props }) {
    const width = 500;
    const height = SIZE_TO_HEIGHT[props.size] || 300;
    const project = useMemo(
        () => fitProjector(unionBbox(blocks.map(b => b.bbox)), width, height, props.padding),
        [blocks, width, height, props.padding]
    );

    const cats = useMemo(() => {
        if (!props.showFaults || props.faultColorBy !== 'category') return [];
        const set = new Set();
        faults.forEach(f => set.add(decodeURIComponent(f.category || '').trim()));
        return Array.from(set).slice(0, 12);
    }, [faults, props.showFaults, props.faultColorBy]);

    return (
        <div style={{ position: 'relative', marginBottom: props.margin }}>
            <svg
                width='100%'
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                style={{ display: 'block', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}
            >
                {/* Outline (if present) */}
                {props.showOutline && Array.isArray(blockOutline?.features) && blockOutline.features.map((f, i) => (
                    <path key={`outline-${i}`} d={featurePath(f, project)}
                        fill="#8a8a8aff" stroke="#333" strokeWidth="1.5" opacity="0.7"
                        vectorEffect="non-scaling-stroke" shapeRendering="crispEdges" />
                ))}

                {/* Blocks */}
                {props.showBlocks && blocks.map(b => (
                    <path key={`b-${b.id}`} d={featurePath(b.feature, project)}
                        fill="#8a8a8aff" stroke="#333" strokeWidth="1.5"
                        vectorEffect="non-scaling-stroke" shapeRendering="crispEdges" />
                ))}

                {/* Block labels */}
                {props.showBlockLabels && blocks.map(b => {
                    const p = project(b.centroid.lon, b.centroid.lat);
                    return (
                        <text key={`lbl-${b.id}`} x={p.x} y={p.y}
                            textAnchor="middle" dominantBaseline="middle"
                            style={{ fontSize: 12, fill: '#334155', fontWeight: 600 }}>
                            {b.id}
                        </text>
                    );
                })}

                {/* Faults */}
                {props.showFaults && faults.map((f, i) => {
                    const lon = Number(f.longitude ?? f.lng), lat = Number(f.latitude ?? f.lat);
                    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
                    const { x, y } = project(lon, lat);
                    const lvl = String(f.level || '').trim() || 'Unknown';
                    const cat = decodeURIComponent(f.category || '').trim();
                    const color = props.faultColorBy === 'level'
                        ? (LEVEL_COLORS[lvl] || '#374151')
                        : hashColor(cat);
                    return <rect
                        key={i}
                        x={x - props.faultSize}
                        y={y - props.faultSize}
                        width={props.faultSize * 2.7}
                        height={props.faultSize * 1.7}
                        fill={color}
                        opacity="0.7"
                    />
                        ;
                })}
            </svg>

            {props.legend && (
                <Legend
                    mode={props.faultColorBy}
                    levels={props.levels}
                    categoriesShown={cats}
                />
            )}
        </div>
    );
}

const ibPanelCache = new Map();

function BlockTile({ ib, feature, panelsSource, faultsForIB, props }) {
    const width = 500;
    const height = (BLOCK_TILE_H[props.size] || 220);
    const bbox = React.useMemo(() => bboxOf(feature), [feature]);
    const project = React.useMemo(
        () => fitProjector(bbox, width, height, props.padding),
        [bbox, width, height, props.padding]
    );

    const [panels, setPanels] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!props.showPanelsInBlocks) { setPanels(null); return; }

        if (panelsSource && typeof panelsSource === 'object' && panelsSource.type === 'FeatureCollection') {
            setPanels(panelsSource);
            return;
        }

        if (typeof panelsSource === 'string' && panelsSource) {
            let cancelled = false;
            (async () => {
                if (ibPanelCache.has(panelsSource)) {
                    setPanels(ibPanelCache.get(panelsSource));
                    return;
                }
                setLoading(true);
                try {
                    const res = await fetch(panelsSource);
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const geo = await res.json();
                    if (!cancelled) {
                        ibPanelCache.set(panelsSource, geo);
                        setPanels(geo);
                    }
                } catch {
                } finally {
                    if (!cancelled) setLoading(false);
                }
            })();
            return () => { cancelled = true; };
        }

        setPanels(null);
    }, [panelsSource, props.showPanelsInBlocks]);

    const faultsByKey = React.useMemo(() => {
        const m = new Map();
        (faultsForIB || []).forEach(f => {
            const k = normalizeKey(f.panelId || f.location || f.ID);
            if (k) m.set(k, f);
        });
        return m;
    }, [faultsForIB]);

    const panelPaths = React.useMemo(() => {
        if (!props.showPanelsInBlocks || !panels?.features) return null;
        return panels.features.map((feat, idx) => {
            const d = featurePath(feat, project);
            const key = normalizeKey(
                feat.properties?.location || feat.properties?.id || feat.id || `__IDX_${idx}`
            );
            const f = key ? faultsByKey.get(key) : null;
            const fill = f ? (LEVEL_COLORS[String(f.level || '').trim()] || props.panelFill) : props.panelFill;
            const stroke = f ? fill : props.panelStroke;
            const sw = f ? 1.2 : 0.8;
            return <path key={key || idx} d={d} fill={fill} stroke={stroke} strokeWidth={sw}
                vectorEffect="non-scaling-stroke" shapeRendering="crispEdges" />;
        });
    }, [panels, project, faultsByKey, props.showPanelsInBlocks, props.panelFill, props.panelStroke]);

    const faultDots = React.useMemo(() => {
        if (!props.showFaults) return null;
        return (faultsForIB || []).map((f, i) => {
            const lon = Number(f.longitude ?? f.lng), lat = Number(f.latitude ?? f.lat);
            if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
            const { x, y } = project(lon, lat);
            const lvl = String(f.level || '').trim() || 'Unknown';
            const cat = decodeURIComponent(f.category || '').trim();
            const color = props.faultColorBy === 'level' ? (LEVEL_COLORS[lvl] || '#374151') : hashColor(cat);
            return <circle key={i} cx={x} cy={y} r={props.faultSize} fill={color} opacity="0.9"
                shapeRendering="geometricPrecision" />;
        });
    }, [faultsForIB, project, props.showFaults, props.faultColorBy, props.faultSize]);

    return (
        <div style={{ position: 'relative', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
            <div style={{ position: 'absolute', left: 10, top: 8, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                {ib} <span style={{ fontWeight: 400 }}>({faultsForIB?.length || 0} faults)</span>
            </div>

            <svg width='100%' height={height} viewBox={`0 0 ${width} ${height}`}>
                <path d={featurePath(feature, project)} fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.2" />
                {panelPaths}
                {faultDots}
            </svg>

            {loading && (
                <div style={{
                    position: 'absolute', top: 8, right: 8, background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 6, padding: '2px 6px', fontSize: 12, color: '#64748b'
                }}>loading…</div>
            )}
        </div>
    );
}

function panelsSourceForBlock(ibs, blockId) {
    if (!ibs) return null;
    const k = String(blockId);
    return ibs[k] ?? ibs[`IB${k}`] ?? null;
}

function BlocksGrid({ outline, ibUrlMap: ibs, faults, props }) {
    const { blocks } = useBlockFeatures(outline);

    const pick = new Set((props.selectedBlocks || []).map(String));
    const chosen = blocks.filter(b => pick.size ? pick.has(String(b.id)) : false);
    const limited = props.blocksLimit ? chosen.slice(0, props.blocksLimit) : chosen;

    const faultsByIB = React.useMemo(() => {
        const m = new Map();
        blocks.forEach(b => m.set(String(b.id), []));
        (faults || []).forEach(f => {
            const ib = faultIBfromLocation(f.location || f.panelId || "");
            if (ib) {
                const key = ib.replace(/^IB/i, '');
                m.get(key)?.push(f);
            }
        });
        return m;
    }, [blocks, faults]);

    if (!chosen.length) {
        return (
            <div style={{
                border: '1px dashed #d1d5db', borderRadius: 12, padding: 24, color: '#6b7280',
                marginBottom: props.margin, background: '#fafafa'
            }}>
                Select blocks in the edit panel to load panel geometry.
            </div>
        );
    }

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.max(1, props.blocksPerRow || 2)}, 1fr)`,
            gap: 12,
            marginBottom: props.margin
        }}>
            {limited.map(b => (
                <BlockTile
                    key={b.id}
                    ib={b.id}
                    feature={b.feature}
                    panelsSource={panelsSourceForBlock(ibs, b.id)}
                    faultsForIB={faultsByIB.get(String(b.id)) || []}
                    props={props}
                />
            ))}
        </div>
    );
}


export function useMapData(activeDoc) {
    const siteName = activeDoc?.siteName || null;
    const [state, setState] = React.useState({ ibs: null, blockOutline: null });

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!siteName) return;

            try {
                const [ibs, blockOutline] = await Promise.all([
                    fetchGeoJsonData(siteName),
                    fetchBlockOutlineData(siteName),
                ]);

                if (!cancelled) {
                    setState({
                        ibs: ibs || {},
                        blockOutline: blockOutline || null,
                    });
                    if (ibs) {
                        console.debug('[MapSection] Loaded IBs:', Object.keys(ibs));
                    }
                }
            } catch (e) {
                if (!cancelled) setState({ ibs: {}, blockOutline: null });
            }
        })();

        return () => { cancelled = true; };
    }, [siteName]);

    return state;
}

/* MapSection + snapshot */

export function MapSection({
    instanceId,
    active,
    siteMeta,
    mapInputs,
    faults,
    updateSectionProps,
    editing = false,
    exportMode = false,
}) {
    const p = {
        ...MAP_DEFAULT_PROPS,
        ...(active?.sectionPropsById?.[instanceId] || {})
    };

    const inputs = mapInputs || {};
    const blockOutline = inputs?.blockOutline || null;
    const { blocks } = useBlockFeatures(blockOutline);
    const faultsShown = useFilteredFaults(faults || [], p);

    if (exportMode && p.snapshotSvg) {
        return (
            <div
                style={{ marginBottom: p.margin }}
                dangerouslySetInnerHTML={{ __html: p.snapshotSvg }}
            />
        );
    }

    // Live render
    const content = p.mode === "blocks" ? (
        <BlocksGrid
            outline={blockOutline}
            ibUrlMap={inputs.ibs || {}}
            faults={faultsShown}
            props={p}
        />
    ) : (
        <OverviewSVG
            blockOutline={blockOutline}
            blocks={blocks}
            faults={faultsShown}
            props={p}
        />
    );

    console.debug('[MapSection] ibs keys:', mapInputs?.ibs && Object.keys(mapInputs.ibs));
    return (
        <div style={{ position: "relative" }} data-map-root data-instance-id={instanceId}>
            {content}
        </div>
    );
}

/* Edit Panel */
export function MapEditPanel({
    props,
    onChange,
    availableBlocks = [],
    onDone,
}) {
    const p = props || MAP_DEFAULT_PROPS;
    const set = (part) => onChange?.(part);
    const setLevel = (k, v) => set({ levels: { ...(p.levels || {}), [k]: v } });

    return (
        <div className="mapTile-panel" onClick={(e) => e.stopPropagation()}>
            <div className="mapTile-header">
                <div className="mapTile-title">Map settings</div>
                <div className="mapTile-subtitle">Tune how the map renders in your report</div>
            </div>

            {/* Mode */}
            <div className="mapTile-row">
                <label className="mapTile-label">Mode</label>
                <div className="mapTile-seg" role="group" aria-label="Map mode">
                    <button
                        type="button"
                        className={`mapTile-segBtn ${p.mode === 'overview' ? 'on' : ''}`}
                        aria-pressed={p.mode === 'overview'}
                        onClick={() => {
                            const next = { mode: 'overview' };
                            // if faultSize is 0 because we auto-zeroed it, restore previous size (if we saved one)
                            if ((p.faultSize ?? 0) === 0 && typeof p._prevFaultSize === 'number') {
                                next.faultSize = p._prevFaultSize;
                                next._prevFaultSize = undefined;
                            }
                            set(next);
                        }}
                    >
                        overview
                    </button>

                    <button
                        type="button"
                        className={`mapTile-segBtn ${p.mode === 'blocks' ? 'on' : ''}`}
                        aria-pressed={p.mode === 'blocks'}
                        onClick={() => {
                            const next = { mode: 'blocks' };
                            // only auto-zero when transitioning into blocks and the current size isn't already 0
                            const current = typeof p.faultSize === 'number' ? p.faultSize : 4; // whatever your default is
                            if (current !== 0) {
                                next._prevFaultSize = current; // stash so we can restore when leaving blocks
                                next.faultSize = 0;
                            }
                            set(next);
                        }}
                    >
                        blocks
                    </button>
                </div>
            </div>

            {/* Size */}
            <div className="mapTile-row">
                <label className="mapTile-label">Map Size</label>
                <div className="mapTile-seg">
                    {['small', 'medium', 'large'].map(s => (
                        <button
                            key={s}
                            className={`mapTile-segBtn ${p.size === s ? 'on' : ''}`}
                            onClick={() => set({ size: s })}
                            type="button"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* Bottom margin */}
            <div className="mapTile-row">
                <label className="mapTile-label">Bottom margin</label>
                <div className="mapTile-rangeWrap">
                    <input
                        type="range" min="0" max="48" step="1"
                        value={Number(p.margin ?? 12)}
                        onChange={(e) => set({ margin: Number(e.target.value) })}
                        className="mapTile-range"
                    />
                    <span className="mapTile-mono">{p.margin}px</span>
                </div>
            </div>

            {/* Show toggles */}
            <div className="mapTile-row">
                <label className="mapTile-label">Show</label>

                {/* segmented toggle pills instead of checkboxes */}
                <div className="mapTile-seg" role="group" aria-label="Show toggles">
                    {[
                        { key: 'showOutline', label: 'Outline' },
                        { key: 'showBlocks', label: 'Blocks' },
                        { key: 'showBlockLabels', label: 'Block labels' },
                        { key: 'showFaults', label: 'Faults' },
                        { key: 'legend', label: 'Legend' },
                    ].map(({ key, label }) => {
                        const on = !!p[key];
                        return (
                            <button
                                key={key}
                                type="button"
                                className={`mapTile-segBtn ${on ? 'on' : ''}`}
                                aria-pressed={on}
                                onClick={() => set({ [key]: !on })}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>


            {/* Fault color by */}
            <div className="mapTile-row">
                <label className="mapTile-label">Color by</label>
                <div className="mapTile-seg">
                    {['level', 'category'].map(m => (
                        <button
                            key={m}
                            className={`mapTile-segBtn ${p.faultColorBy === m ? 'on' : ''}`}
                            onClick={() => set({ faultColorBy: m })}
                            type="button"
                        >
                            {m}
                        </button>
                    ))}
                </div>
            </div>

            {/* Fault size */}
            <div className="mapTile-row">
                <label className="mapTile-label">Fault size</label>
                <div className="mapTile-rangeWrap">
                    <input
                        type="range" min="2" max="10" step="1"
                        value={Number(p.faultSize ?? 4)}
                        onChange={(e) => set({ faultSize: Number(e.target.value) })}
                        className="mapTile-range"
                    />
                    <span className="mapTile-mono">{p.faultSize}px</span>
                </div>
            </div>

            {/* Levels */}
            <div className="mapTile-row">
                <label className="mapTile-label">Levels</label>

                <div className="mapTile-seg" role="group" aria-label="Level toggles">
                    {['Level 1', 'Level 2', 'Level 3'].map((L) => {
                        const on = p.levels?.[L] !== false;
                        return (
                            <button
                                key={L}
                                type="button"
                                className={`mapTile-segBtn ${on ? 'on' : ''}`}
                                aria-pressed={on}
                                onClick={() => setLevel(L, !on)}
                            >
                                {L}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Blocks mode extras */}
            {p.mode === 'blocks' && (
                <>
                    <div className="mapTile-row">
                        <label className="mapTile-label">Blocks per row</label>
                        <div className="mapTile-rangeWrap">
                            <input
                                type="range" min="1" max="4" step="1"
                                value={Number(p.blocksPerRow ?? 2)}
                                onChange={(e) => set({ blocksPerRow: Number(e.target.value) })}
                                className="mapTile-range"
                            />
                            <span className="mapTile-mono">{p.blocksPerRow}</span>
                        </div>
                    </div>

                    <div className="mapTile-row">
                        <label className="mapTile-label">Select blocks</label>

                        <div className="mapTile-scrollbox">
                            {availableBlocks.length === 0 ? (
                                <div className="mapTile-muted">No blocks found</div>
                            ) : (
                                <>
                                    {/* toolbar */}
                                    <div className="mapTile-chipToolbar">
                                        <div className="mapTile-chipToolbarLeft">
                                            <button
                                                type="button"
                                                className="mapTile-segBtn"
                                                onClick={() => set({ selectedBlocks: [...availableBlocks] })}
                                            >
                                                All
                                            </button>
                                            <button
                                                type="button"
                                                className="mapTile-segBtn"
                                                onClick={() => set({ selectedBlocks: [] })}
                                            >
                                                None
                                            </button>
                                            <button
                                                type="button"
                                                className="mapTile-segBtn"
                                                onClick={() => {
                                                    const cur = new Set(p.selectedBlocks || []);
                                                    const next = availableBlocks.filter(b => !cur.has(b));
                                                    set({ selectedBlocks: next });
                                                }}
                                            >
                                                Invert
                                            </button>
                                        </div>
                                        <div className="mapTile-chipCount">
                                            {(p.selectedBlocks || []).length} selected
                                        </div>
                                    </div>

                                    {/* chip grid */}
                                    <div className="mapTile-chipGrid" role="group" aria-label="Block selection">
                                        {availableBlocks.map((ib) => {
                                            const on = (p.selectedBlocks || []).includes(ib);
                                            return (
                                                <button
                                                    key={ib}
                                                    type="button"
                                                    className={`mapTile-chip ${on ? 'on' : ''}`}
                                                    aria-pressed={on}
                                                    onClick={() => {
                                                        const next = new Set(p.selectedBlocks || []);
                                                        on ? next.delete(ib) : next.add(ib);
                                                        set({ selectedBlocks: Array.from(next) });
                                                    }}
                                                    title={String(ib)}
                                                >
                                                    {/* show just the number if present; otherwise the id */}
                                                    {ib?.toString().match(/\d+/)?.[0] ?? ib}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                </>
            )}

            <div className="mapTile-row">
                {/* Actions */}
                <div className="mapTile-actions">
                    <button
                        type="button"
                        className="btn save"
                        onClick={() => onDone?.()}
                        title="Done"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: '1px solid #1d6a2e',
                            background: '#2e7d32',
                            color: '#fffbed',
                            cursor: 'pointer',
                            fontWeight: 600,
                        }}
                    >
                        <CheckIcon fontSize="small" />
                        Done
                    </button>
                </div>
            </div>

        </div>
    );
}

